import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BillingServiceSelector from '../Interface/Services/BillingServiceSelector';
import CheckOutServices from '../Interface/Sales/CheckOutServices';
import generateThermalReceiptESCPOS from '../Interface/utils/generateThermalReceiptESCPOS';
import Swal from 'sweetalert2';
import { message } from 'antd';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Container, Avatar, Typography } from '@mui/material';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';

import '../../Styles/Controllers/ReservationController.css';

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function toMySQLDatetime(dateString) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function BillingServiceController({ dressList, employee, suitList }) {
  const [step, setStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [services, setServices] = useState([]);
  const [arrOfServiceTypes, setArrOfServiceTypes] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedServicesTotal, setSelectedServicesTotal] = useState(null);
  const [handleReqEventsError, setHandleReqEventsError] = useState('');
  const [dressReservations, setDressReservations] = useState([]);
  const [suitReservations, setSuitReservations] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [clients, setClients] = useState([]);
  const [printerConfig, setPrinterConfig] = useState({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
  const [configLoading, setConfigLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [banksOptions, setBanksOptions] = useState([]);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [extensionChecked, setExtensionChecked] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in';
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getDressReservations = async () => {
    let token = localStorage.getItem('token');
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/list-dress-reservations', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error:', errorData.message);
        return setHandleReqEventsError(errorData.message);
      }
      const data = await response.json();
      return setDressReservations(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getSuitReservations = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/list-suit-reservations', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error:', errorData.message);
        return setHandleReqEventsError(errorData.message);
      }
      const data = await response.json();
      return setSuitReservations(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const requestServices = async () => {
    let token = localStorage.getItem('token');
    if (!token) {
      return navigate('/sign-in');
    }
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/services-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        return navigate('/sign-in');
      }
      const data = await response.json();
      return setServices(data);
    } catch (error) {
      messageApi.error('Error al obtener la lista de servicios.');
    }
  };

  const requestServicesTypes = async () => {
    let token = localStorage.getItem('token');
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceTypes-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          messageApi.error('Sesión expirada, por favor inicia sesión de nuevo.');
        }
        throw new Error('Error al obtener los tipos de servicios');
      }
      const data = await response.json();
      if (data.length > 0) {
        let arr = data.filter(serviceType => serviceType.serviceType !== 'Inventario' && serviceType.serviceType !== 'Reservacion');

        return setArrOfServiceTypes(arr);
      }
      return setArrOfServiceTypes(data);
    } catch (error) {
      messageApi.error('Error al obtener los tipos de servicios.');
    }
  };

  const requestClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/clients-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Error al obtener los clientes');
      }
      const data = await response.json();
      return setClients(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBankAccounts = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/billing-services/bankAccounts/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBanksOptions(data);
      } else {
        messageApi.error('Error al cargar las cuentas bancarias.');
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error de red al cargar las cuentas bancarias.');
    }
  };

  const postFormValues = async (formValues) => {
    const token = localStorage.getItem('token');
    try {
      const confirmReq = await Swal.fire({
        title: 'Confirmar facturación',
        text: '¿Estás seguro/a de que deseas facturar estos servicios?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonColor: '#d32f2f',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Facturar',
        confirmButtonColor: '#4caf50'
      });

      if (confirmReq.isConfirmed) {
        Swal.fire({
          title: 'Procesando factura',
          text: 'Por favor espera mientras procesamos tu factura.',
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        for (const service of formValues.selectedServices) {
          service.totalPrice = (parseNumber(service.servicePrice) * service.quantity) * (1 - (parseNumber(service.discountApplied) || 0) / 100);
        }
        const response = await fetch('https://dprincess-training.servehttp.com/billing-services-create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formValues)
        });
        if (response.ok) {
          const data = await response.json();

          if (!data || !data.invoice) {
            console.error('Invalid response structure:', data);
            Swal.close();
            await Swal.fire({
              title: 'Error en respuesta del servidor',
              text: 'La factura se ha procesado con éxito pero la respuesta del servidor no contiene los datos esperados para realizar la impresión de la factura.',
              icon: 'error',
              confirmButtonText: 'Entendido'
            });
            return window.location.reload();
          }

          const invoice = data.invoice;
          messageApi.loading({ content: 'Imprimiendo factura...', key: 'invoiceProcessing', duration: 2 });

          const groupServicesBySelectedServiceID = (invoiceDetails) => {
            if (!invoiceDetails || !Array.isArray(invoiceDetails)) {
              return [];
            }
            const grouped = {};
            invoiceDetails.forEach(detail => {
              const key = detail.selectedServiceID;
              if (grouped[key]) {
                grouped[key].amount = (parseNumber(grouped[key].amount) + parseNumber(detail.amount))?.toString();
                grouped[key].serviceQty = detail.serviceQty;
                if (Array.isArray(grouped[key].cashFlowID)) {
                  grouped[key].cashFlowID.push(detail.cashFlowID);
                } else {
                  grouped[key].cashFlowID = [grouped[key].cashFlowID, detail.cashFlowID];
                }
                grouped[key].description = grouped[key].description
              } else {
                grouped[key] = { ...detail, amount: detail.amount?.toString() };
              }
            });
            return Object.values(grouped);
          };

          let paidAmount = 0;
          let reservationPaid = [];

          if (invoice.invoiceAllDetails && Array.isArray(invoice.invoiceAllDetails) && invoice.invoiceAllDetails.length > 0) {
            const invoiceDetails = groupServicesBySelectedServiceID(invoice.invoiceAllDetails);
            invoice.invoiceDetails = invoiceDetails;
            for (const details of invoice.invoiceAllDetails) {
              if (details && details.amount) {
                paidAmount += parseNumber(details.amount);
                if (details.serviceID === 24) {
                  details.amount = parseNumber(details.amount);
                  reservationPaid.push(details);
                }
              }
            }
          }
          else {
            const comboPrice = invoice.comboPrice ? parseNumber(invoice.comboPrice) : 0;
            paidAmount = comboPrice;
            reservationPaid = comboPrice > 0 ? [{ serviceID: 24, amount: comboPrice }] : [];
          }

          const totalAmount = invoice.totalAmount ? parseNumber(invoice.totalAmount) : 0;
          invoice.pendingAmount = totalAmount - paidAmount;
          invoice.paidAmount = paidAmount;
          invoice.reservationPaid = reservationPaid;

          if (invoice && (invoice.invoiceDetails || invoice.comboPrice)) {
            const printResult = await handleDirectThermalPrint(invoice);
            try {
              if (printResult.success) {
                Swal.close();
                return await Swal.fire({
                  title: 'Facturación exitosa',
                  text: 'Los servicios han sido facturados correctamente y la impresión se ha realizado con éxito.',
                  icon: 'success',
                  confirmButtonText: 'Aceptar'
                }).then(() => { window.location.reload(); });
              } else {
                messageApi.warning('Servicios facturados exitosamente, pero hubo un error al imprimir.');
                Swal.close();
                return await Swal.fire({
                  title: 'Facturación exitosa',
                  text: 'Los servicios han sido facturados correctamente, pero ocurrió un error al imprimir la factura.',
                  icon: 'warning',
                  confirmButtonText: 'Entendido'
                }).then(() => { window.location.reload(); });
              }
            } catch (error) {
              console.error('Error:', error);
              Swal.close();
              return await Swal.fire({
                title: 'Error al procesar la factura',
                text: 'Los servicios han sido facturados, pero ocurrió un error inesperado con la impresión de la factura.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
              }).then(() => { window.location.reload(); });
            }
          }
        }
        else {
          Swal.close();
          return await Swal.fire({
            title: 'Error facturando los servicios',
            text: 'Ocurrió un error al registrar la facturación, por favor intenta de nuevo.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.close();
      return await Swal.fire({
        title: 'Error facturando los servicios',
        text: 'Ocurrió un error en la facturación:' + error,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const fetchAgentPrinterConfiguration = async () => {
    if (isMobile) return;
    setConfigLoading(true);
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para obtener configuración de impresora.');
      setConfigLoading(false);
      return;
    }

    try {
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/printer-config',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response && response.success) {
        try {
          let config;
          if (typeof response.data === 'string') {
            config = JSON.parse(response.data);
          } else {
            config = response.data;
          }
          setPrinterConfig(config);
          if (config.IsPrinterSelected) {
            messageApi.success(`Configuración de impresora '${config.SelectedPrinterName}' (${config.SuggestedCharsPerLine} car.) obtenida.`);
          } else {
            messageApi.info(`Agente no tiene impresora predeterminada. Usando ancho ${config.SuggestedCharsPerLine} car.`);
          }
        } catch (parseError) {
          console.error('Error al parsear configuración de impresora:', parseError);
          messageApi.error('Error al procesar la configuración de impresora recibida.');
          setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
        }
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error(`Failed to get printer config via extension: ${errorMsg}`);
        messageApi.error("Fallo al obtener configuración de impresora del agente via extensión.");
        setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
      }
    } catch (error) {
      console.error("Error en fetchAgentPrinterConfiguration via extensión:", error);
      messageApi.error("Error de conexión al contactar agente para configuración de impresora via extensión.");
      setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleDirectThermalPrint = async (invoice) => {
    if (isMobile) return messageApi.warning('La impresión térmica no está disponible en dispositivos móviles.');
    if (!invoice) {
      messageApi.error('No hay datos de factura para imprimir.');
      return;
    }

    // Chequear la extensión en tiempo real antes de imprimir
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      console.log('Extensión no instalada para handleDirectThermalPrint');
      return { success: false, message: 'La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para imprimir.' };
    }

    messageApi.loading({ content: 'Enviando recibo térmico a la impresora...', key: 'thermalPrint' });

    const charsPerLine = printerConfig?.SuggestedCharsPerLine || 48;
    const escposData = await generateThermalReceiptESCPOS(invoice, charsPerLine);

    try {
      console.log('Enviando proxyRequest para /print via extensión');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/print',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          data: escposData,
          printerName: printerConfig?.IsPrinterSelected ? printerConfig.SelectedPrinterName : null,
          printType: 'RAW'
        }
      });

      if (response && response.success) {
        messageApi.success({ content: `Recibo térmico enviado: ${response.data}`, key: 'thermalPrint', duration: 3 });
        return { success: true, message: `Documento enviado: ${response.data}` };
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        messageApi.error({ content: `Error de conexión: ${errorMsg}`, key: 'thermalPrint', duration: 5 });
        return { success: false, message: errorMsg };
      }
    } catch (error) {
      console.error('Error al enviar el recibo térmico via extensión:', error);
      messageApi.error({ content: 'Error de conexión con el agente térmico. Asegúrese de que esté en ejecución.', key: 'thermalPrint', duration: 5 });
      return { success: false, message: 'Error de conexión con la impresora. Verifique que esté conectada y el agente en ejecución.' };
    }
  };

  const handleServicesInvoice = (services) => {
    const servicesCashFlow = [];
    for (const service of services) {
      servicesCashFlow.push({
        amount: service.amountPaid,
        paymentMethod: paymentMethod,
        description: "Pago por servicio " + service.serviceName,
        flowType: 'ingreso',
        serviceID: service.serviceID,
        creationDate: toMySQLDatetime(new Date())
      });
    }
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      serviceCashFlow: servicesCashFlow
    }));
  };

  const updateSelectedServices = (allSelectedServices) => {
    setSelectedServices(allSelectedServices);
    const formValuesCopy = { ...formValues };
    formValuesCopy.selectedServices = allSelectedServices;
    setFormValues(formValuesCopy);
  };

  const additionalXVDresses = (dresses) => {
    const formValuesCopy = { ...formValues };
    formValuesCopy.additionalXVDresses = dresses;
    setFormValues(formValuesCopy);
  };

  const additionalWDresses = (dresses) => {
    const formValuesCopy = { ...formValues };
    formValuesCopy.additionalXVDresses = dresses;
    setFormValues(formValuesCopy);
  };

  const previousStepHandler = () => {
    messageApi.info('Regresando al paso anterior...');
    if (step > 0) {
      return setStep(step - 1);
    }
  }
  const nextStepHandler = () => {
    if (step === 0) {
      setStep(1);
    }
  }
  const handleSelectedServicesTotal = (total) => {
    formValues.reservation.servicesTotal = total;
    setSelectedServicesTotal(total);
  };

  const handleClientSelection = (client) => {
    const formValuesCopy = { ...formValues };
    formValuesCopy.client = client;
    setFormValues(formValuesCopy);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    if (method === 'transferencia') {
      fetchBankAccounts(); // Llama a la función para obtener bancos
    }
    setFormValues(prevFormValues => {
      const updatedFormValues = {
        ...prevFormValues,
        paymentMethod: method // Guardamos el método de pago general
      };
      if (updatedFormValues.serviceCashFlow && Array.isArray(updatedFormValues.serviceCashFlow)) {
        updatedFormValues.serviceCashFlow = updatedFormValues.serviceCashFlow.map(service => ({
          ...service,
          paymentMethod: method
        }));
      }
      return updatedFormValues;
    });
  };

  const handleBankChange = (bankId) => {
    setFormValues(prev => ({ ...prev, bank: bankId }));
  };

  const handleReferenceChange = (reference) => {
    setFormValues(prev => ({ ...prev, reference: reference }));
  };

  // --- FUNCIÓN MODIFICADA ---
  // Ahora maneja tanto vestidos como trajes
  const handleScheduleDate = (eventDate, item, service, action = 'add', oldItem = null) => {
    setFormValues(prevFormValues => {
      const formValuesCopy = { ...prevFormValues };
      let currentEvents = formValuesCopy.event ? [...formValuesCopy.event] : [];
      // Mantiene las reservas de vestidos y trajes en listas separadas
      let currentDressReservations = formValuesCopy.dressReservations ? [...formValuesCopy.dressReservations] : [];
      let currentSuitReservations = formValuesCopy.suitReservations ? [...formValuesCopy.suitReservations] : [];

      const isDress = !!(item?.dressID || oldItem?.dressID);
      const itemID = isDress ? (item?.dressID || oldItem?.dressID) : (item?.suitID || oldItem?.suitID);
      const idKey = isDress ? 'dressID' : 'suitID';

      if (action === 'remove') {
        currentEvents = currentEvents.filter(e => e[idKey] !== itemID);
        if (isDress) {
          currentDressReservations = currentDressReservations.filter(dr => dr.dressID !== itemID);
        } else {
          currentSuitReservations = currentSuitReservations.filter(sr => sr.suitID !== itemID);
        }
      } else if (action === 'remove-all') {
        currentEvents = currentEvents.filter(e => e.serviceID !== service.serviceID);
        if (isDress) {
          currentDressReservations = currentDressReservations.filter(dr => dr.serviceID !== service.serviceID);
        } else {
          currentSuitReservations = currentSuitReservations.filter(sr => sr.serviceID !== service.serviceID);
        }
      } else if (action === 'update' && oldItem) {
        const oldItemID = isDress ? oldItem.dressID : oldItem.suitID;
        currentEvents = currentEvents.filter(e => e[idKey] !== oldItemID);
        if (isDress) {
          currentDressReservations = currentDressReservations.filter(dr => dr.dressID !== oldItemID);
        } else {
          currentSuitReservations = currentSuitReservations.filter(sr => sr.suitID !== oldItemID);
        }
      }

      if ((action === 'add' || action === 'update') && eventDate && item && service) {
        const itemName = isDress ? item.dressName : item.suitName;
        const newItemType = isDress ? 'vestido' : 'traje';

        const newEvent = {
          eventDateStart: eventDate.eventDateStart,
          eventDateEnd: eventDate.eventDateEnd,
          eventType: 'rental',
          eventLocation: 'Rentado a cliente',
          eventTitle: `Renta de ${newItemType}`,
          status: 'pendiente',
          paymentStatus: 'completado',
          eventDescription: `Renta de ${newItemType} ${itemName}`,
          [idKey]: itemID,
          serviceID: service.serviceID,
        };

        const newReservation = {
          eventDateStart: eventDate.eventDateStart,
          eventDateEnd: eventDate.eventDateEnd,
          [idKey]: itemID,
          status: 'pendiente',
          serviceID: service.serviceID,
          ...(isDress ? { dressName: itemName } : { suitName: itemName }),
        };

        currentEvents.push(newEvent);
        if (isDress) {
          currentDressReservations.push(newReservation);
        } else {
          currentSuitReservations.push(newReservation);
        }
      }

      return {
        ...prevFormValues,
        event: currentEvents,
        dressReservations: currentDressReservations,
        suitReservations: currentSuitReservations,
      };
    });
  };


  const handlePostRequest = async () => {
    postFormValues(formValues);
  };

  const sendToExtension = async (message) => {
    console.log('window.thermalPrinterProxy exists:', !!window.thermalPrinterProxy);
    console.log('window.thermalPrinterProxy value:', window.thermalPrinterProxy);

    if (window.thermalPrinterProxy) {
      // Usar función global inyectada por la extensión
      console.log('Usando función global thermalPrinterProxy');
      const response = await window.thermalPrinterProxy.sendMessage(message);

      // Verificar si la respuesta es una instancia de Response (de fetch)
      if (response instanceof Response) {
        try {
          if (response.ok) {
            const data = await response.text();
            return { success: true, data };
          } else {
            const error = await response.text();
            return { success: false, error };
          }
        } catch (e) {
          console.error('Error al leer el body de la Response:', e);
          return { success: false, error: 'Error al procesar la respuesta de la extensión: ' + e.message };
        }
      } else {
        // Asumir que es un objeto con { success, data, error }
        return response;
      }
    } else {
      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage:', extensionId);

      if (extensionId) {
        console.log('Usando chrome.runtime.sendMessage con ID dinámico:', extensionId);
        return new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
              if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                resolve(response);
              }
            }
          );
        });
      } else {
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return { success: false, error: 'No se encontró el ID de la extensión en localStorage.' };
      }
    }
  };

  // Función para chequear la extensión en tiempo real (actualizada)
  const checkExtensionRealTime = async () => {
    console.log('Iniciando checkExtensionRealTime...');
    if (window.thermalPrinterProxy) {
      console.log('Función global thermalPrinterProxy disponible');
      return true;
    }
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      console.log('Chrome runtime no disponible');
      return false;
    }

    // Obtener ID dinámico de localStorage
    const extensionId = localStorage.getItem('thermalPrinterExtensionId');
    console.log('Extension ID from localStorage:', extensionId);

    if (!extensionId) {
      console.log('No hay ID en localStorage, extensión no disponible');
      return false;
    }

    try {
      console.log('Enviando ping a extensión con ID dinámico:', extensionId);
      const response = await new Promise((resolve, reject) => {
        chrome.runtime.sendMessage(
          extensionId,
          { action: 'ping' },
          (response) => {
            if (chrome.runtime.lastError) {
              reject(new Error(chrome.runtime.lastError.message));
            } else {
              resolve(response);
            }
          }
        );
      });
      console.log('Respuesta de extensión al ping:', response);
      return response && response.success;
    } catch (error) {
      console.error('Error verificando extensión en tiempo real:', error);
      return false;
    }
  };

  useEffect(() => {
    requestServices();
    const checkExtension = async () => {
      if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('La extensión no está instalada o no se puede acceder.');
        return;
      }

      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage en useEffect:', extensionId);

      if (!extensionId) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return;
      }

      try {
        const response = await new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId,
            { action: 'ping' },
            (response) => {
              if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                resolve(response);
              }
            }
          );
        });

        setExtensionInstalled(response && response.success);
      } catch (error) {
        console.error('Error verificando extensión:', error);
        messageApi.error('Error al verificar la extensión: ' + error.message);
        setExtensionInstalled(false);
      } finally {
        setExtensionChecked(true);
      }
    };

    checkExtension();
  }, []);

  useEffect(() => {
    if (extensionChecked) {
      fetchAgentPrinterConfiguration();
    }
  }, [extensionChecked]);

  useEffect(() => {
    requestServicesTypes();
  }, []);
  useEffect(() => {
    requestClients();
  }, []);

  useEffect(() => {
    if (arrOfServiceTypes.length <= 0) {
      requestServicesTypes();
    }
  }, [arrOfServiceTypes]);

  useEffect(() => {
    if (formValues.length > 0 && step === 0) {
      setStep(1);
    }
  }, [formValues]);

  useEffect(() => {
    getDressReservations();
    getSuitReservations();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  };

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div className='reservation-main-container' style={{ display: 'flex', flexDirection: 'column', width: '100%', height: 'calc(100vh - 74px)', overflow: 'hidden' }}>
      {contextHolder}
      <div className='reservation-container'>
        {
          step === 0 && <BillingServiceSelector
            selectedServices={selectedServices}
            dressReservations={dressReservations}
            clientsList={clients}
            handleClientSelection={handleClientSelection}
            nextStepHandler={nextStepHandler}
            step={step}
            updateSelectedServices={updateSelectedServices}
            services={services}
            arrOfServiceTypes={arrOfServiceTypes}
            client={formValues.client}
            dressList={dressList}
            additionalXVDresses={additionalXVDresses}
            additionalWDresses={additionalWDresses}
            handleServicesInvoice={handleServicesInvoice}
            handleScheduleDate={handleScheduleDate}
            employee={employee}
            requestClients={requestClients}
            // --- NUEVAS PROPS ---
            // Se pasan los datos de trajes al selector
            suitList={suitList}
            suitReservations={suitReservations}
            // Se unifican las reservaciones nuevas para el diálogo
            newDressReservation={formValues.dressReservations?.concat(formValues.suitReservations || [])}
          />
        }
        {
          step === 1 && <CheckOutServices formValues={formValues} selectedServices={selectedServices} previousStepHandler={previousStepHandler}
            handleSelectedServicesTotal={handleSelectedServicesTotal}
            handlePostRequest={handlePostRequest} employee={employee} handlePaymentMethodChange={handlePaymentMethodChange}
            banksOptions={banksOptions}
            handleBankChange={handleBankChange}
            handleReferenceChange={handleReferenceChange}
          />
        }
      </div>
    </div>
  )
}

export default BillingServiceController